/**
 * Factory function to create an empty UnscheduledEvent template.
 * Used when creating a new record in the EventModal.
 */
import { UnscheduledEvent } from '../../types';

/**
 * Returns the current UTC timestamp in "YYYY-MM-DD HH:MM:SS" format.
 */
function getCurrentUtcTimestamp(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  const hours = String(now.getUTCHours()).padStart(2, '0');
  const minutes = String(now.getUTCMinutes()).padStart(2, '0');
  const seconds = String(now.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/** The DEA maintenance-barrier start/end time fields. */
type DeaBarrierFields = Pick<
  UnscheduledEvent,
  | 'deaBarrierPartsStartTime'
  | 'deaBarrierPartsEndTime'
  | 'deaBarrierToolingStartTime'
  | 'deaBarrierToolingEndTime'
  | 'deaBarrierManpowerStartTime'
  | 'deaBarrierManpowerEndTime'
  | 'deaBarrierEquipmentStartTime'
  | 'deaBarrierEquipmentEndTime'
  | 'deaBarrierDarkHoursStartTime'
  | 'deaBarrierDarkHoursEndTime'
  | 'deaBarrierOtherStartTime'
  | 'deaBarrierOtherEndTime'
>;

/** Returns the DEA maintenance-barrier time fields, all defaulted to empty. */
function emptyDeaBarrierFields(): DeaBarrierFields {
  return {
    deaBarrierPartsStartTime: '',
    deaBarrierPartsEndTime: '',
    deaBarrierToolingStartTime: '',
    deaBarrierToolingEndTime: '',
    deaBarrierManpowerStartTime: '',
    deaBarrierManpowerEndTime: '',
    deaBarrierEquipmentStartTime: '',
    deaBarrierEquipmentEndTime: '',
    deaBarrierDarkHoursStartTime: '',
    deaBarrierDarkHoursEndTime: '',
    deaBarrierOtherStartTime: '',
    deaBarrierOtherEndTime: '',
  };
}

/**
 * Creates an empty UnscheduledEvent template for new record creation.
 * Auto-populates creationTime with the current UTC timestamp.
 */
export function createEmptyEvent(): UnscheduledEvent {
  return {
    eventId: '',
    recordType: '',
    status: '',
    tailNumber: '',
    carrier: '',
    station: '',
    inboundFlightNumber: '',
    outboundFlightNumber: '',
    nextScheduledFlightNumber: '',
    nextScheduledFlightTimeZulu: '',
    previousFlightScheduledDepartureTimeZulu: '',
    chargeableFlightScheduledDepartureTimeZulu: '',
    nextAdviseTimeZulu: '',
    returnToServiceTimeZulu: '',
    completionTimeZulu: '',
    deferralReference: '',
    delayTimeMinutes: 0,
    notes: '',
    correctiveAction: '',
    creationTime: getCurrentUtcTimestamp(),
    lastUpdateTime: '',
    returnToBlock: false,
    abortedTakeOff: false,
    airTurnBack: false,
    diversion: false,
    cancellation: false,
    delay: false,
    damage: false,
    chargeable: false,
    ataCodeId: 0,
    deaImpactedPackageCount: 0,
    projectedDeaImpact: 0,
    ...emptyDeaBarrierFields(),
    summary: '',
    revisionCounter: 0,
    highPriorityEvent: false,
    initialReturnToServiceTimeZulu: '',
    returnToServiceChangeReason: '',
    timeDeclaredZulu: '',
    returnToServiceConfidence: '',
  };
}
